import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from 'services/api';
import { extractErrorMessage } from 'helpers/extractErrorMessage';

export interface DropdownParameter {
  parameter_id: number;
  node_instance_id: number;
  name: string; // used as the UI label/key
  parameter_type: 'Dropdown' | string;
  default_expression: string | null;
  value: string | null;
  runtime: boolean;
  submit_value: string | null; // user's last submitted value (if any)
  out_value: string | null;
  inout: 'in' | 'out' | 'inout' | string;
  parent_parameter_out: number | null;
  options: string; // comma-separated options
}

// Define the shape of a Workflow and WorkflowAction
interface WorkflowAction {
  workflow_action_id: number;
  workflow_id: number;
  action_type: string;
  action_details: Record<string, any>;
  response?: { id: string; label: string; user?: string; timestamp: number };
  response_options?: Array<{
    id: string | number;
    label: string;
    handHandle?: boolean;
  }>;
  response_inputs?: Array<{ label: string; value: string }>;
  response_input_options?: Array<{ id: string; label: string }>;
  response_variables?: Array<{ label: string; value: string }>;
  response_variables_options?: Array<{
    id: string;
    type: string;
    label: string;
    options: string;
    default_value: string;
  }>;
  dropdown_parameters?: DropdownParameter[];
  created_at: string;
  modified_at: string;
  subject: string;
  message: string;
}

interface Workflow {
  workflow_id: number;
  workflow_data: any;
  workflow_category: string;
  node_instance_id: number;
  node_id: string;
  workflow_subject: string;
  chosen_option?: { id: string; key: string };
  response_options?: Array<{ id: string; key: string }>;
  workflows_actions: WorkflowAction[];
  created_at: string;
  updated_at: string;
}

interface WorkflowsPagination {
  total_records: number;
  total_pages: number;
  current_page: number;
  limit: number;
  offset: number;
}

interface WorkflowState {
  workflows: Workflow[];
  filterOptions: string[];
  pagination: WorkflowsPagination | null;
  loading: boolean;
  createLoading: boolean;
  updateLoading: boolean;
  deleteLoading: boolean;
  error: string | null;
}

// Initial state for workflows slice
const initialState: WorkflowState = {
  workflows: [],
  filterOptions: [],
  pagination: null,
  loading: false,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,
  error: null,
};

// Thunks for asynchronous operations

// Fetch all workflows assigned to the current user
export const fetchWorkflows = createAsyncThunk<
  {
    workflows: Workflow[];
    filterOptions: string[];
    pagination: WorkflowsPagination | null;
  },
  | string
  | {
      filter?: string;
      page?: number;
      limit?: number;
      sortOrder?: 'asc' | 'desc';
    }
  | undefined,
  { rejectValue: string }
>('workflows/fetchWorkflows', async (payload, { rejectWithValue }) => {
  try {
    const filter = typeof payload === 'string' ? payload : payload?.filter;
    const page = typeof payload === 'object' ? payload?.page : undefined;
    const limit = typeof payload === 'object' ? payload?.limit : undefined;
    const sortOrder = typeof payload === 'object' ? payload?.sortOrder : undefined;
    const response = await api.get('/workflows', {
      params: {
        filter: filter || 'all',
        page: page || 1,
        limit: limit || 25,
        sortOrder: sortOrder || 'desc',
      },
    });
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data || 'Error fetching workflows');
  }
});

// Respond to a workflow action with a chosen option
export const respondToWorkflowAction = createAsyncThunk<WorkflowAction, { workflow_action_id: number; chosen_id: string | number }>(
  'workflows/respondToWorkflowAction',
  async ({ workflow_action_id, chosen_id }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/workflows/respond/${workflow_action_id}`, { chosen_id: String(chosen_id) });
      return response.data as WorkflowAction;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error, 'Error responding to workflow action'));
    }
  }
);

// Save workflow action with filled response inputs
export const saveWorkflowAction = createAsyncThunk<
  WorkflowAction,
  {
    workflow_action_id: number;
    response_inputs: Array<{ label: string; value: string }>;
    dropdown_parameters?: Array<{ parameter_id: number; value: string }>;
  }
>('workflows/saveWorkflowAction', async ({ workflow_action_id, response_inputs, dropdown_parameters }, { rejectWithValue }) => {
  try {
    const response = await api.put(`/workflowActions/${workflow_action_id}`, {
      response_inputs,
      dropdown_parameters,
    });
    return response.data as WorkflowAction;
  } catch (error) {
    return rejectWithValue(extractErrorMessage(error, 'Error saving workflow action'));
  }
});

export const markWorkflowAsRead = createAsyncThunk<{ workflow_id: number }, number, { rejectValue: string }>(
  'workflows/markWorkflowAsRead',
  async (workflowId, { rejectWithValue }) => {
    try {
      await api.put(`/workflows/${workflowId}/read`);
      return { workflow_id: workflowId };
    } catch (err: any) {
      return rejectWithValue(err.response?.data || 'Error marking workflow as read');
    }
  }
);

// Workflows slice
const workflowsSlice = createSlice({
  name: 'workflows',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch workflows
      .addCase(fetchWorkflows.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWorkflows.fulfilled, (state, action) => {
        state.loading = false;
        const arg = action.meta.arg;
        const requestedPage = typeof arg === 'object' && arg?.page ? arg.page : 1;

        if (requestedPage > 1) {
          const merged = [...state.workflows];
          const existingIds = new Set(state.workflows.map((workflow) => workflow.workflow_id));

          action.payload.workflows.forEach((workflow) => {
            if (!existingIds.has(workflow.workflow_id)) {
              merged.push(workflow);
            }
          });

          state.workflows = merged;
        } else {
          state.workflows = action.payload.workflows;
        }

        state.filterOptions = action.payload.filterOptions;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchWorkflows.rejected, (state, action) => {
        state.loading = false;
        state.error = extractErrorMessage(action.payload, 'Unknown error');
      })
      .addCase(markWorkflowAsRead.fulfilled, (state, action) => {
        const { workflow_id } = action.payload;
        const index = state.workflows.findIndex((wf) => wf.workflow_id === workflow_id);
        if (index !== -1) {
          if (!state.workflows[index].workflow_data) {
            (state.workflows[index] as any).workflow_data = {};
          }
          (state.workflows[index] as any).workflow_data.read = true;
        }
      })
      // Respond to workflow action
      .addCase(respondToWorkflowAction.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
      })
      .addCase(respondToWorkflowAction.fulfilled, (state, action) => {
        state.updateLoading = false;
        const { workflow_id, workflow_action_id } = action.payload;
        const workflowIndex = state.workflows.findIndex((workflow) => workflow.workflow_id === workflow_id);

        if (workflowIndex !== -1) {
          const workflow = state.workflows[workflowIndex];
          const actionIndex = workflow.workflows_actions.findIndex((act) => act.workflow_action_id === workflow_action_id);
          if (actionIndex !== -1) {
            // Update the workflow action with the chosen option
            workflow.workflows_actions[actionIndex] = action.payload;
          }
        }
      })
      .addCase(respondToWorkflowAction.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = extractErrorMessage(action.payload, 'Unknown error');
      })

      // Save workflow action with response inputs
      .addCase(saveWorkflowAction.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
      })
      .addCase(saveWorkflowAction.fulfilled, (state, action) => {
        state.updateLoading = false;
        const { workflow_id, workflow_action_id } = action.payload;
        const workflowIndex = state.workflows.findIndex((workflow) => workflow.workflow_id === workflow_id);

        if (workflowIndex !== -1) {
          const workflow = state.workflows[workflowIndex];
          const actionIndex = workflow.workflows_actions.findIndex((act) => act.workflow_action_id === workflow_action_id);
          if (actionIndex !== -1) {
            // Update the workflow action with the saved response inputs
            workflow.workflows_actions[actionIndex] = action.payload;
          }
        }
      })
      .addCase(saveWorkflowAction.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = extractErrorMessage(action.payload, 'Unknown error');
      });
  },
});

export default workflowsSlice.reducer;
